// src/pages/About/index.jsx (完整代码，包含图表功能)
import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Modal, 
  Avatar, 
  Typography, 
  Tag, 
  Space, 
  Card, 
  Statistic, 
  Row, 
  Col, 
  Divider, 
  message 
} from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Link } from 'react-router-dom';
import { usePostContext } from '../../App';
import 'antd/dist/reset.css';
import './index.css';
import { defaultUserInfo } from '../../api/mockData';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const About = () => {
  const { posts, loggedInUser } = usePostContext();
  // 游客默认信息
  const defaultGuestInfo = {
    avatarUrl: 'https://tuhenmei.com/zb_users/upload/2023/06/2023061316866172392.jpg',
    name: '游客',
    intro: '请登录后完善个人信息',
    detailIntro: '登录后可编辑您的个人资料',
    email: '暂无',
    techStack: '暂无',
    hobbies: '暂无',
    github: '#',
    homepage: '#'
  };

  const [userInfo, setUserInfo] = useState(defaultGuestInfo);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  // 计算用户统计数据
  const userPosts = posts.filter(post => post.author === loggedInUser?.username);
  // src/pages/About/index.jsx (关键部分)
// 在 About 组件中，修改 userStats 计算部分：
const userStats = {
  totalPosts: userPosts.length,
  totalViews: userPosts.reduce((sum, post) => sum + (post.views || 0), 0),
  totalLikes: userPosts.reduce((sum, post) => sum + (post.likeCount || 0), 0),
  joinDate: loggedInUser?.joinDate || '2024-01-01' // 使用用户的实际加入日期
};
  // 按月份统计文章数量
  const monthlyPostData = () => {
    const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
    const data = months.map(month => ({ name: month, count: 0 }));
    
    userPosts.forEach(post => {
      const date = new Date(post.publishTime);
      const month = date.getMonth();
      data[month].count += 1;
    });
    
    return data;
  };

  // 按类别统计文章
  const categoryData = () => {
    const categoryMap = {};
    userPosts.forEach(post => {
      const category = post.category || '未分类';
      categoryMap[category] = (categoryMap[category] || 0) + 1;
    });
    
    return Object.entries(categoryMap).map(([name, value]) => ({ name, value }));
  };

  // 按标签统计文章
  const tagData = () => {
    const tagMap = {};
    userPosts.forEach(post => {
      if (Array.isArray(post.tags)) {
        post.tags.forEach(tag => {
          tagMap[tag] = (tagMap[tag] || 0) + 1;
        });
      }
    });
    
    return Object.entries(tagMap)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));
  };

  // 从localStorage读取用户信息
  const getUserInfoFromStorage = (username) => {
    const savedUserInfos = localStorage.getItem('userProfiles');
    if (!savedUserInfos) return null;
    const userProfiles = JSON.parse(savedUserInfos);
    return userProfiles[username] || null;
  };

  // 保存用户信息到localStorage
  const saveUserInfoToStorage = (username, info) => {
    const savedUserInfos = localStorage.getItem('userProfiles');
    const userProfiles = savedUserInfos ? JSON.parse(savedUserInfos) : {};
    userProfiles[username] = info;
    localStorage.setItem('userProfiles', JSON.stringify(userProfiles));
  };

  // 登录状态变化时加载信息
  useEffect(() => {
    if (loggedInUser) {
      const savedInfo = getUserInfoFromStorage(loggedInUser.username);
      const initInfo = savedInfo 
        ? savedInfo 
        : { ...defaultGuestInfo, ...defaultUserInfo, ...loggedInUser };
      setUserInfo(initInfo);
    } else {
      setUserInfo(defaultGuestInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  // 打开编辑弹窗（现在会被按钮触发）
  const handleEdit = () => {
    if (!loggedInUser) return;
    setIsModalOpen(true);
    form.setFieldsValue({
      avatarUrl: userInfo.avatarUrl,
      name: userInfo.name,
      intro: userInfo.intro,
      detailIntro: userInfo.detailIntro,
      email: userInfo.email,
      techStack: userInfo.techStack,
      hobbies: userInfo.hobbies,
      github: userInfo.github,
      homepage: userInfo.homepage
    });
  };

  // 提交编辑表单
  const handleFinish = (values) => {
    const newUserInfo = {
      ...userInfo,
      ...values,
      techStack: values.techStack || '暂无',
      hobbies: values.hobbies || '暂无',
    };
    setUserInfo(newUserInfo);
    if (loggedInUser) {
      saveUserInfoToStorage(loggedInUser.username, newUserInfo);
      message.success('个人信息更新成功！');
    }
    setIsModalOpen(false);
  };

  // 社交分享功能
  const handleShareProfile = async () => {
    const shareData = {
      title: `关于 ${userInfo.name}`,
      text: userInfo.intro,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('分享失败:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => message.success('链接已复制到剪贴板'))
        .catch(() => message.error('复制失败'));
    }
  };

  // 显示最近发布的文章
  const recentPosts = userPosts
    .sort((a, b) => new Date(b.publishTime) - new Date(a.publishTime))
    .slice(0, 3);

  return (
    <div className="about-container">
      {!loggedInUser ? (
        // 未登录提示
        <div className="about-not-login">
          <p className="about-not-login-text">请登录后查看和编辑个人信息</p>
          <Link to="/login">
            <Button type="primary">立即登录</Button>
          </Link>
        </div>
      ) : (
        // 已登录显示个人信息
        <>
          {/* 用户统计信息 */}
          <div className="about-stats-section">
            <Row gutter={16} justify="center" className="about-stats-row">
              <Col span={6}>
                <Card className="about-stat-card">
                  <Statistic 
                    title="文章数" 
                    value={userStats.totalPosts} 
                    prefix={<span className="about-stat-icon">📝</span>}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card className="about-stat-card">
                  <Statistic 
                    title="总访问量" 
                    value={userStats.totalViews} 
                    prefix={<span className="about-stat-icon">👁️</span>}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card className="about-stat-card">
                  <Statistic 
                    title="总点赞数" 
                    value={userStats.totalLikes} 
                    prefix={<span className="about-stat-icon">👍</span>}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card className="about-stat-card">
                  <Statistic 
                    title="加入日期" 
                    value={userStats.joinDate} 
                    prefix={<span className="about-stat-icon">📅</span>}
                  />
                </Card>
              </Col>
            </Row>
          </div>

          {/* 图表区域 */}
          {userStats.totalPosts > 0 && (
            <div className="about-charts-section">
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="每月文章发布统计">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={monthlyPostData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#8884d8" name="文章数" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="文章分类统计">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={categoryData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {categoryData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
              </Row>
              
              {tagData().length > 0 && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Card title="标签统计">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={tagData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="value" fill="#82ca9d" name="使用次数" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                </Row>
              )}
            </div>
          )}

          {/* 个人信息头部 */}
          <div className="about-infoHeader">
            <Avatar
              src={userInfo.avatarUrl}
              size={100}
              className="about-avatar"
              fallback={<span>{userInfo.name?.charAt(0) || 'P'}</span>}
            />
            <div className="about-nameIntro">
              <h1 className="about-nameTitle">Hi，我是 {userInfo.name}！</h1>
              <p className="about-introText">{userInfo.intro}</p>
              <p className="about-detailIntroText">{userInfo.detailIntro}</p>
            </div>
          </div>

          {/* 个人信息表格 */}
          <div className="about-tableWrap">
            <table className="about-infoTable">
              <thead>
                <tr>
                  <th className="about-tableTh">信息项</th>
                  <th className="about-tableTh">内容</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="about-tableTdLabel">邮箱</td>
                  <td className="about-tableTdValue">{userInfo.email || '暂无'}</td>
                </tr>
                <tr>
                  <td className="about-tableTdLabel">技术栈</td>
                  <td className="about-tableTdValue">
                    {userInfo.techStack.split(',').map((tech, index) => (
                      <Tag key={index} color="blue" className="about-tech-tag">
                        {tech.trim()}
                      </Tag>
                    ))}
                  </td>
                </tr>
                <tr>
                  <td className="about-tableTdLabel">兴趣爱好</td>
                  <td className="about-tableTdValue">
                    {userInfo.hobbies.split(',').map((hobby, index) => (
                      <Tag key={index} color="green" className="about-hobby-tag">
                        {hobby.trim()}
                      </Tag>
                    ))}
                  </td>
                </tr>
                <tr>
                  <td className="about-tableTdLabel">GitHub 链接</td>
                  <td className="about-tableTdValue">
                    {userInfo.github !== '#' ? (
                      <a href={userInfo.github} target="_blank" rel="noopener noreferrer">
                        {userInfo.github}
                      </a>
                    ) : (
                      '暂无'
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="about-tableTdLabel">个人主页链接</td>
                  <td className="about-tableTdValue">
                    {userInfo.homepage !== '#' ? (
                      <a href={userInfo.homepage} target="_blank" rel="noopener noreferrer">
                        {userInfo.homepage}
                      </a>
                    ) : (
                      '暂无'
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 最近发布的文章 */}
          {recentPosts.length > 0 && (
            <>
              <Divider />
              <div className="about-recent-posts">
                <Title level={3} className="about-section-title">最近发布的文章</Title>
                <div className="about-posts-list">
                  {recentPosts.map(post => (
                    <Card key={post.id} className="about-post-card" hoverable>
                      <div className="about-post-header">
                        <Title level={4} className="about-post-title">
                          <Link to={`/postdetail/${post.id}`}>{post.title}</Link>
                        </Title>
                        <Text type="secondary" className="about-post-meta">
                          {post.publishTime} · 👁️{post.views || 0} · 👍{post.likeCount || 0}
                        </Text>
                      </div>
                      <Paragraph className="about-post-summary" ellipsis={{ rows: 2 }}>
                        {post.summary || '暂无摘要，点击查看全文...'}
                      </Paragraph>
                      <div className="about-post-footer">
                        <Space size="small" className="about-post-tags">
                          {Array.isArray(post.tags) 
                            ? post.tags.map(tag => <Tag key={tag} size="small">{tag}</Tag>) 
                            : <Tag size="small">{post.tags}</Tag>
                          }
                        </Space>
                        <Link to={`/postdetail/${post.id}`} className="about-post-link">
                          阅读全文 →
                        </Link>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* 编辑和分享按钮区域 */}
          <div className="about-action-section">
            <Space size="middle">
              <Button type="primary" onClick={handleEdit} className="about-editBtn">
                编辑个人信息
              </Button>
              <Button onClick={handleShareProfile} className="about-shareBtn">
                分享个人资料
              </Button>
              <Link to="/home">
                <Button>返回首页</Button>
              </Link>
            </Space>
          </div>
        </>
      )}

      {/* 编辑个人信息模态框 */}
      <Modal
        title="编辑个人信息"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark="*"
          className="about-modalForm"
        >
          <Form.Item
            name="avatarUrl"
            label="头像 (URL)"
            rules={[{ required: true, message: '请输入头像URL' }]}
          >
            <Input placeholder="请输入头像图片的URL地址" />
          </Form.Item>
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入您的姓名" />
          </Form.Item>
          <Form.Item
            name="intro"
            label="个人简介"
            rules={[{ required: true, message: '请输入个人简介' }]}
          >
            <TextArea rows={3} placeholder="请输入个人简介" />
          </Form.Item>
          <Form.Item
            name="detailIntro"
            label="更详细的介绍"
            rules={[{ required: true, message: '请输入更详细的介绍' }]}
          >
            <TextArea rows={4} placeholder="请输入更详细的个人介绍" />
          </Form.Item>
          <Form.Item
            name="email"
            label="联系邮箱"
            rules={[
              { required: true, message: '请输入联系邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入您的联系邮箱" />
          </Form.Item>
          <Form.Item 
            name="techStack" 
            label="技术栈"
            help="多个技术栈请用逗号分隔，例如：React,JavaScript,CSS"
          >
            <Input placeholder="请输入您的技术栈，用逗号分隔" />
          </Form.Item>
          <Form.Item 
            name="hobbies" 
            label="兴趣爱好"
            help="多个兴趣爱好请用逗号分隔，例如：阅读,编程,旅行"
          >
            <Input placeholder="请输入您的兴趣爱好，用逗号分隔" />
          </Form.Item>
          <Form.Item name="github" label="GitHub 链接">
            <Input placeholder="请输入您的GitHub地址" />
          </Form.Item>
          <Form.Item name="homepage" label="个人主页链接 (可选)">
            <Input placeholder="请输入您的个人主页地址" />
          </Form.Item>
          <Form.Item className="about-formFooter">
            <Button onClick={() => setIsModalOpen(false)}>取消</Button>
            <Button type="primary" htmlType="submit">确定</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default About;